import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-location';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import cacheKeys from 'Core/hooks/queries/cacheKeys';

import useUpdateExistingAccount from 'Modules/patientfi/hooks/useUpdateExistingAccount';
import useGetApplication from 'Modules/patientfi/hooks/useGetApplication';
import { createFinancingPath } from 'Modules/patientfi/routes';
import constants from 'Modules/patientfi/config/constants';

export default function useExistingAccountSubmit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scroll = useScrollTop();
  const queryClient = useQueryClient();

  const [requestId, setRequestId] = React.useState('');
  const [polling, setPolling] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);

  const { mutateAsync: updateExistingAccount, isLoading: isSubmitting } =
    useUpdateExistingAccount();
  const { isSuccess, data: polledApp } = useGetApplication(requestId, polling ? 3000 : false);

  React.useEffect(() => {
    if (isSuccess && polling && polledApp?.next_action) {
      sessionStorage.setItem(constants.SESS_STORAGE_APPLICATION_KEY, JSON.stringify(polledApp));
      queryClient.invalidateQueries(cacheKeys.financingNextStep);
      setRequestId('');
      setPolling(false);
      navigate({ to: createFinancingPath, search: true, replace: true });
    }
  }, [isSuccess, polledApp, polling, navigate, queryClient]);

  React.useEffect(
    () => () => {
      queryClient.removeQueries(cacheKeys.patientfiGetApplication);
    },
    [queryClient]
  );

  const submit = async (requestIncrease) => {
    setSubmitError(null);
    setRequestId('');
    scroll.top();
    try {
      setPolling(true);
      const {
        headers: { request_id },
      } = await updateExistingAccount({ request_increase: requestIncrease });
      setRequestId(request_id);
    } catch (error) {
      setPolling(false);
      setSubmitError(
        error?.response?.data?.message ||
          t(
            'patientfi.existingAccount.error.requestFailed',
            'We could not continue with your PatientFi account. Please try again.'
          )
      );
    }
  };

  return { submit, submitError, busy: isSubmitting || polling };
}
